import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, UserPlus } from 'lucide-react';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

export default function NouveauCollaborateur() {
  const navigate = useNavigate();
  const { secteurs: contextSecteurs, managers: contextManagers } = useApp();
  const [secteurs, setSecteurs] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    matricule: '',
    prenom: '',
    nom: '',
    email: '',
    secteur_id: '',
    poste: '',
    manager_id: '',
    manager_nom: '',
    telephone: '',
    date_embauche: ''
  });

  // Load secteurs and managers
  useEffect(() => {
    const loadData = async () => {
      try {
        if (contextSecteurs && contextSecteurs.length > 0) {
          setSecteurs(contextSecteurs);
        } else {
          const data = await api.getSecteurs();
          setSecteurs(data || []);
        }
        
        if (contextManagers && contextManagers.length > 0) {
          setManagers(contextManagers);
        } else {
          const users = await api.getUtilisateurs();
          const mgrs = users.filter(u => u.actif !== false && u.role !== 'Collaborateur');
          setManagers(mgrs || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, [contextSecteurs, contextManagers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Add manager name if manager is selected
      const selectedManager = managers.find(m => m.id === formData.manager_id);
      const dataToSubmit = {
        ...formData,
        manager_nom: selectedManager ? `${selectedManager.prenom} ${selectedManager.nom}` : formData.manager_nom
      };
      
      const newCollab = await api.createCollaborateur(dataToSubmit);
      toast.success('Collaborateur créé avec succès');
      navigate(`/collaborateurs/${newCollab.id}`);
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/collaborateurs')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nouveau Collaborateur</h1>
          <p className="text-gray-500">Ajouter un nouveau collaborateur au système</p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Informations du collaborateur
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="matricule">Matricule *</Label>
                <Input
                  id="matricule"
                  value={formData.matricule}
                  onChange={(e) => setFormData({ ...formData, matricule: e.target.value })}
                  placeholder="MAT-2024-001"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="prenom.nom@entreprise.fr"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="prenom">Prénom *</Label>
                <Input
                  id="prenom"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  placeholder="Jean"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nom">Nom *</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Dupont"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="secteur">Secteur *</Label>
                <Select 
                  value={formData.secteur_id} 
                  onValueChange={(value) => setFormData({ ...formData, secteur_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un secteur" />
                  </SelectTrigger>
                  <SelectContent>
                    {secteurs.map((secteur) => (
                      <SelectItem key={secteur.id} value={secteur.id}>
                        {secteur.libelle}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="poste">Poste *</Label>
                <Input
                  id="poste"
                  value={formData.poste}
                  onChange={(e) => setFormData({ ...formData, poste: e.target.value })}
                  placeholder="Opérateur, Chef d'équipe..."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="manager">Manager responsable</Label>
                <Select 
                  value={formData.manager_id || "none"} 
                  onValueChange={(value) => setFormData({ ...formData, manager_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="manager-select">
                    <SelectValue placeholder="Sélectionner un responsable" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun responsable</SelectItem>
                    {managers && managers.length > 0 ? (
                      managers.map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{manager.prenom} {manager.nom}</span>
                            <span className="text-muted-foreground text-xs px-1.5 py-0.5 bg-gray-100 rounded">
                              {manager.role}
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="loading" disabled>
                        Chargement des utilisateurs...
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Sélectionnez le responsable hiérarchique de ce collaborateur
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="telephone">Téléphone</Label>
                <Input
                  id="telephone"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  placeholder="06 12 34 56 78"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date_embauche">Date d'embauche</Label>
                <Input
                  id="date_embauche"
                  type="date"
                  value={formData.date_embauche}
                  onChange={(e) => setFormData({ ...formData, date_embauche: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => navigate('/collaborateurs')}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading} className="gap-2">
                {loading ? (
                  <><span className="animate-spin">...</span> Création...</>
                ) : (
                  <><Save className="h-4 w-4" /> Créer le collaborateur</>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
